import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckSquare, 
  faSquare,
  faChevronDown,
  faCircle,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

const DomainSelector = ({ 
  domains, 
  selectedDomains, 
  onDomainToggle, 
  onSelectAll,
  getDomainScore,
  theme 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const allSelected = selectedDomains.length === domains.length;
  const someSelected = selectedDomains.length > 0 && !allSelected;

  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '1.5rem',
      border: `1px solid ${theme?.primary?.gray?.[300]}`,
      boxShadow: theme?.shadows?.base,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isExpanded ? '1.5rem' : 0,
      cursor: 'pointer',
      padding: '0.5rem 0',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.primary?.gray?.[900],
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    badge: {
      background: theme?.primary?.orange?.[100],
      color: theme?.primary?.orange?.[700],
      padding: '0.25rem 0.75rem',
      borderRadius: '1rem',
      fontSize: '0.875rem',
      fontWeight: '600',
    },
    expandIcon: {
      color: theme?.primary?.gray?.[500],
      transition: 'transform 0.3s ease',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    },
    selectAllContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      background: theme?.primary?.gray?.[50],
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: `1px solid ${theme?.primary?.gray?.[200]}`,
    },
    domainGrid: {
      display: 'grid',
      gap: '0.75rem',
    },
    domainItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      borderRadius: '0.75rem',
      background: theme?.primary?.gray?.[100],
      border: `0px solid ${theme?.primary?.gray?.[200]}`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    domainItemHover: {
      background: theme?.primary?.gray?.[100],
      transform: 'translateX(5px)',
      borderColor: theme?.primary?.gray?.[300],
    },
    domainContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flex: 1,
    },
    checkbox: {
      fontSize: '1.25rem',
      color: theme?.primary?.orange?.[500],
    },
    checkboxUnchecked: {
      fontSize: '1.25rem',
      color: theme?.primary?.gray?.[400],
    },
    domainName: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: theme?.primary?.gray?.[900],
      flex: 1,
    },
    scoreIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '0.5rem',
      background: theme?.primary?.gray?.[100],
    },
    scoreDot: {
      fontSize: '0.5rem',
    },
    scoreText: {
      fontSize: '0.75rem',
      fontWeight: '600',
    },
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div 
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 style={styles.title}>
          <FontAwesomeIcon icon={faLayerGroup} style={{ color: theme?.primary?.orange?.[500] }} />
          Select Domains
          <span style={styles.badge}>
            {selectedDomains.length} / {domains.length}
          </span>
        </h2>
        <FontAwesomeIcon icon={faChevronDown} style={styles.expandIcon} />
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              style={styles.selectAllContainer}
              whileHover={{ scale: 1.02, backgroundColor: theme?.primary?.gray?.[100] }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectAll(!allSelected)}
            >
              <FontAwesomeIcon 
                icon={allSelected ? faCheckSquare : someSelected ? faSquare : faSquare} 
                style={{
                  ...styles.checkbox,
                  opacity: someSelected ? 0.5 : 1
                }}
              />
              <span style={styles.domainName}>
                {allSelected ? 'Deselect All' : 'Select All Domains'}
              </span>
            </motion.div>
            
            <div style={styles.domainGrid}>
              {domains.map((domain, index) => {
                const isSelected = selectedDomains.some(d => d.id === domain.id);
                const { score, color } = getDomainScore(domain.id);
                
                return (
                  <motion.div
                    key={domain.id}
                    style={styles.domainItem}
                    whileHover={styles.domainItemHover}
                    onClick={() => onDomainToggle(domain)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div style={styles.domainContent}>
                      <FontAwesomeIcon 
                        icon={isSelected ? faCheckSquare : faSquare} 
                        style={isSelected ? styles.checkbox : styles.checkboxUnchecked}
                      />
                      <span style={styles.domainName}>{domain.domain_name}</span>
                    </div>
                    
                    <div style={styles.scoreIndicator}>
                      <FontAwesomeIcon 
                        icon={faCircle} 
                        style={{ ...styles.scoreDot, color }}
                      />
                      <span style={{ ...styles.scoreText, color }}>
                        {score}%
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DomainSelector;